import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { UploadSimple, Warning, CheckCircle } from "@phosphor-icons/react";

export default function CsvUploadDialog({ open, onOpenChange, onSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setCommitting(false);
  };

  const runPreview = async (f) => {
    if (!f) return;
    setFile(f);
    setPreview(null);
    setLoading(true);
    try {
      const p = await api.importCsvPreview(f);
      setPreview(p);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Preview failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    if (!window.confirm(
      "This will REPLACE ALL routes, staff, assignments, absences, vehicles and services with the uploaded CSV. Continue?"
    )) return;
    setCommitting(true);
    try {
      const r = await api.importCsvCommit(file);
      toast.success(
        `Imported: ${r.routes} routes · ${r.staff} staff · ${r.assignments} assignments`
      );
      onOpenChange(false);
      reset();
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Import failed";
      toast.error(msg);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent
        className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#0A0A0A] max-w-2xl"
        data-testid="csv-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight">
            UPLOAD SCHEDULE CSV
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-[11px] font-mono-plex text-zinc-600 leading-relaxed">
            Same shape as the bundled seed: row 4 = dates in D/M/YY, rows 5+ = route (col B) ·
            position (col C) · staff cells from col D onwards.
          </div>

          {/* File picker */}
          <div className="border-2 border-dashed border-black p-6 text-center bg-zinc-50">
            <input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => runPreview(e.target.files?.[0])}
              data-testid="csv-file-input"
              className="hidden"
            />
            <label
              htmlFor="csv-file"
              className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white cursor-pointer hover:bg-zinc-800 font-mono-plex text-xs tracking-widest active:scale-[0.98]"
            >
              <UploadSimple size={14} weight="bold" />
              {file ? "CHANGE FILE" : "CHOOSE CSV"}
            </label>
            {file && (
              <div className="mt-3 font-mono-plex text-[11px]">
                <span className="text-zinc-500">FILE:</span>{" "}
                <span className="font-semibold">{file.name}</span>{" "}
                <span className="text-zinc-500">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            )}
          </div>

          {loading && (
            <div className="font-mono-plex text-xs text-zinc-500 tracking-widest">
              PARSING PREVIEW…
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="border-2 border-black" data-testid="csv-preview">
              <div className="px-3 py-2 border-b-2 border-black bg-black text-white font-mono-plex text-[10px] tracking-widest flex items-center gap-2">
                <CheckCircle size={12} weight="bold" />
                PREVIEW · DATA WILL BE REPLACED ON IMPORT
              </div>
              <div className="grid grid-cols-3 border-b border-zinc-300">
                <PreviewStat label="ROUTES" value={preview.routes} />
                <PreviewStat label="STAFF" value={preview.staff} />
                <PreviewStat label="ASSIGNMENTS" value={preview.assignments} />
              </div>
              <div className="px-3 py-2 border-b border-zinc-300 font-mono-plex text-[11px]">
                <span className="text-zinc-500">DATE RANGE:</span>{" "}
                <span className="font-semibold">
                  {preview.date_start} → {preview.date_end}
                </span>
              </div>
              <div className="px-3 py-2 border-b border-zinc-300">
                <div className="font-mono-plex text-[10px] tracking-widest text-zinc-500 mb-1">
                  ROUTE CODES ({preview.route_codes.length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {preview.route_codes.map((c) => (
                    <span
                      key={c}
                      className="font-mono-plex text-[10px] bg-zinc-100 border border-zinc-300 px-1.5 py-0.5"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <div className="px-3 py-2 border-b border-zinc-300">
                <div className="font-mono-plex text-[10px] tracking-widest text-zinc-500 mb-1">
                  STAFF SAMPLE ({preview.staff_sample?.length || 0} of {preview.staff})
                </div>
                <div className="flex flex-wrap gap-1">
                  {(preview.staff_sample || []).map((s) => (
                    <span key={s} className="font-mono-plex text-[10px] text-zinc-700">
                      · {s}
                    </span>
                  ))}
                </div>
              </div>
              {preview.sample_route && (
                <div className="px-3 py-2 font-mono-plex text-[10px]">
                  <span className="text-zinc-500 tracking-widest">SAMPLE ROUTE:</span>{" "}
                  <span className="font-semibold">{preview.sample_route.code}</span>{" "}
                  <span className="text-zinc-500">
                    ({preview.sample_route.group}, 1 D · {preview.sample_route.loader_slots} L)
                  </span>
                  <div className="mt-1 text-zinc-600">
                    First assignments:{" "}
                    {preview.sample_route.sample_assignments
                      .map((a) => `${a.date} ${a.slot}`)
                      .join(", ")}
                  </div>
                </div>
              )}
              <div className="px-3 py-2 border-t-2 border-black bg-amber-50 flex items-center gap-2 font-mono-plex text-[10px] text-amber-900">
                <Warning size={12} weight="bold" />
                Import will wipe existing routes, staff, assignments, absences, vehicles & services.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100"
          >
            CANCEL
          </button>
          <button
            onClick={commit}
            disabled={!preview || committing}
            data-testid="csv-import-confirm"
            className="px-4 py-2 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-50"
          >
            {committing ? "IMPORTING…" : "REPLACE ALL & IMPORT"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewStat({ label, value }) {
  return (
    <div className="px-3 py-2 border-r border-zinc-300 last:border-r-0">
      <div className="font-mono-plex text-[9px] tracking-widest text-zinc-500">{label}</div>
      <div className="font-heading font-black text-2xl leading-none tracking-tight">{value}</div>
    </div>
  );
}
